import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Plus, Package, Minus, RotateCcw, Clock } from 'lucide-react';
import { ModuleWithStatus } from '@/types/marketplace';
import { cn } from '@/lib/utils';
import * as LucideIcons from 'lucide-react';

interface ModuleCardProps {
  module: ModuleWithStatus;
  onSubscribe: (module: ModuleWithStatus) => void;
  onDeactivate?: (module: ModuleWithStatus) => void;
  onReactivate?: (module: ModuleWithStatus) => void;
  isRequesting?: boolean;
}

const ModuleCard = ({ module, onSubscribe, onDeactivate, onReactivate, isRequesting }: ModuleCardProps) => {
  const isActive = module.status === 'active';
  const isIncluded = module.status === 'included';
  const isPendingCancellation = module.status === 'pending_cancellation';

  // Get icon dynamically
  const IconComponent = module.icon 
    ? (LucideIcons as any)[module.icon] || Package
    : Package;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  return (
    <Card 
      className={cn(
        'relative transition-all duration-200 hover:shadow-md',
        isActive && 'border-green-500/50 bg-green-50/30 dark:bg-green-950/10',
        isIncluded && 'border-blue-500/50 bg-blue-50/30 dark:bg-blue-950/10',
        isPendingCancellation && 'border-yellow-500/50 bg-yellow-50/30 dark:bg-yellow-950/10'
      )}
    >
      {isActive && !isPendingCancellation && (
        <div className="absolute -top-2 -right-2">
          <div className="bg-green-500 rounded-full p-1">
            <Check className="h-3 w-3 text-white" />
          </div>
        </div>
      )}

      {isPendingCancellation && (
        <div className="absolute -top-2 -right-2">
          <div className="bg-yellow-500 rounded-full p-1">
            <Clock className="h-3 w-3 text-white" />
          </div>
        </div>
      )}
      
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3">
            <div className={cn(
              'p-2 rounded-lg',
              isActive && !isPendingCancellation ? 'bg-green-100 dark:bg-green-900/30' : 
              isPendingCancellation ? 'bg-yellow-100 dark:bg-yellow-900/30' : 'bg-muted'
            )}>
              <IconComponent className={cn(
                'h-5 w-5',
                isActive && !isPendingCancellation ? 'text-green-600' : 
                isPendingCancellation ? 'text-yellow-600' : 'text-muted-foreground'
              )} />
            </div>
            <div>
              <CardTitle className="text-base">{module.name}</CardTitle>
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {module.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {module.description}
          </p>
        )}

        <div className="flex items-center justify-between">
          <div className="text-sm">
            {isPendingCancellation ? (
              <div className="space-y-1">
                <Badge variant="outline" className="bg-yellow-100 text-yellow-700 border-yellow-300">
                  <Clock className="h-3 w-3 mr-1" />
                  Cancelamento Agendado
                </Badge>
                {module.clientModule?.pending_deactivation_at && (
                  <p className="text-xs text-muted-foreground">
                    Até {formatDate(module.clientModule.pending_deactivation_at)}
                  </p>
                )}
              </div>
            ) : isActive ? (
              <Badge variant="default" className="bg-green-600 hover:bg-green-700">
                <Check className="h-3 w-3 mr-1" />
                Ativo
              </Badge>
            ) : isIncluded ? (
              <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                Incluso no Plano
              </Badge>
            ) : (
              <span className="font-semibold text-foreground">
                {formatPrice(module.price)}<span className="text-xs text-muted-foreground">/lic/mês</span>
              </span>
            )}
          </div>

          {!isActive && !isIncluded && !isPendingCancellation && (
            <Button 
              size="sm" 
              onClick={() => onSubscribe(module)}
              disabled={isRequesting}
            >
              <Plus className="h-4 w-4 mr-1" />
              Aderir
            </Button>
          )}

          {isActive && !isPendingCancellation && onDeactivate && (
            <Button 
              size="sm" 
              variant="outline"
              className="text-destructive border-destructive/30 hover:bg-destructive/10"
              onClick={() => onDeactivate(module)}
              disabled={isRequesting}
            >
              <Minus className="h-4 w-4 mr-1" />
              Desativar
            </Button>
          )}

          {isPendingCancellation && onReactivate && (
            <Button 
              size="sm" 
              variant="outline"
              className="text-green-600 border-green-300 hover:bg-green-50"
              onClick={() => onReactivate(module)}
              disabled={isRequesting}
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Reativar
            </Button>
          )}
        </div>

      </CardContent>
    </Card>
  );
};

export default ModuleCard;
